import { EnterpriseApiSignature, EnterpriseEndpointKey } from "#resources/helpers/api";
import { ResetOptions, useQueryClient } from "@tanstack/react-query";

interface Props<Keys extends EnterpriseEndpointKey> {
	endpoint: Keys;
	params?: Partial<EnterpriseApiSignature<Keys>["args"]>;
}

/**
 * Hook para resetar uma query, removendo dados do cache e voltando ao estado inicial.
 *
 * @template K - O tipo de EnterpriseEndpointKey.
 * @param {Props<K>} props - As props para o hook.
 * @returns {Function} - Uma função que pode ser usada para resetar uma consulta.
 * @example
 * ```ts
 *const resetGetPlaceProductsQuery = useResetQuery({
 *  endpoint: "getPlaceProducts",
 *  params: { placeId: "123" }
 *});
 *
 *resetGetPlaceProductsQuery()
 * ```
 */
export function useResetQuery<K extends EnterpriseEndpointKey>(props: Props<K>) {
	const queryClient = useQueryClient();

	return (
		dynamicParams?: Partial<EnterpriseApiSignature<K>["args"]>,
		options?: ResetOptions,
	) => {
		const combinedParams = dynamicParams
			? { ...props.params, ...dynamicParams }
			: props.params;

		return queryClient.resetQueries(
			{ queryKey: [props.endpoint, combinedParams].filter(Boolean) },
			options,
		);
	};
}
